import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, MapPin, Phone, Mail, ChevronRight, Search } from 'lucide-react';
import { getMosques } from '../api';

export default function MosqueList() {
  const [mosques, setMosques] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMosques()
      .then((res) => {
        setMosques(res.data);
        setFiltered(res.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(
      mosques.filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          m.address.toLowerCase().includes(q)
      )
    );
  }, [search, mosques]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 badge-emerald mb-4">
            <Building2 className="w-4 h-4" />
            All Mosques
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Oldham <span className="gradient-text">Mosques</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">
            Find your nearest mosque in Oldham. Click on any mosque to view prayer times and more details.
          </p>
        </motion.div>

        {/* Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="relative mb-10 max-w-md mx-auto"
        >
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search mosques..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-dark w-full rounded-xl pl-12 pr-4 py-3"
          />
        </motion.div>

        {/* Count */}
        {!loading && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-500 text-sm mb-6 text-center"
          >
            Showing {filtered.length} of {mosques.length} mosques
          </motion.p>
        )}

        {/* Mosque Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="rounded-2xl h-52 shimmer" />
            ))}
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filtered.map((mosque, idx) => (
              <motion.div key={mosque._id} variants={itemVariants}>
                <Link to={`/mosque/${mosque._id}`} className="mosque-card rounded-2xl p-6 flex flex-col h-full block group">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-emerald-700/10 flex items-center justify-center border border-emerald-500/20 group-hover:border-emerald-500/50 transition-colors">
                      <Building2 className="w-6 h-6 text-emerald-400" />
                    </div>
                    <span className="badge-gold text-xs">#{idx + 1}</span>
                  </div>

                  {/* Name */}
                  <h2 className="text-white font-bold text-xl mb-3 group-hover:text-emerald-400 transition-colors leading-tight">
                    {mosque.name}
                  </h2>

                  {/* Details */}
                  <div className="space-y-2 flex-1">
                    <div className="flex items-start gap-2.5 text-gray-400 text-sm">
                      <MapPin className="w-4 h-4 text-emerald-500/60 flex-shrink-0 mt-0.5" />
                      <span>{mosque.address}</span>
                    </div>
                    {mosque.phone && (
                      <div className="flex items-center gap-2.5 text-gray-400 text-sm">
                        <Phone className="w-4 h-4 text-emerald-500/60 flex-shrink-0" />
                        <span>{mosque.phone}</span>
                      </div>
                    )}
                    {mosque.email && (
                      <div className="flex items-center gap-2.5 text-gray-400 text-sm">
                        <Mail className="w-4 h-4 text-emerald-500/60 flex-shrink-0" />
                        <span className="truncate">{mosque.email}</span>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                    <span className="text-emerald-400 text-sm font-medium">View Details</span>
                    <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}

        {filtered.length === 0 && !loading && (
          <div className="text-center py-20 glass-card rounded-2xl">
            <Building2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-white font-semibold text-xl mb-2">No Mosques Found</h3>
            <p className="text-gray-400">Try adjusting your search terms.</p>
          </div>
        )}
      </div>
    </div>
  );
}
